import { TableSettingsState } from "@idw-types/state/table-settings-state";
import { TableSettingsActionTypes, TableSettingsActions } from "../actions/table-settings-actions";
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from "@constants/table-defaults";
import { TableState } from "@tanstack/react-table";

export const initialTableState: TableState = {
    pagination: {
        pageIndex: DEFAULT_PAGE_INDEX,
        pageSize: DEFAULT_PAGE_SIZE
    },
    columnFilters: [],
    sorting: [],
    columnOrder: [],
    columnPinning: {},
    columnSizing: {},
    columnSizingInfo: {
        columnSizingStart: [],
        deltaOffset: null,
        deltaPercentage: null,
        isResizingColumn: false,
        startOffset: null,
        startSize: null
    },
    columnVisibility: {},
    expanded: {},
    globalFilter: [],
    grouping: [],
    rowPinning: {},
    rowSelection: {}
};

export const initialTableSettingsState: TableSettingsState = {
    adaptiveAuthentication: initialTableState,
    alertEvents: initialTableState,
    attributeLabels: initialTableState,
    axnAdminSessions: initialTableState,
    axnSessions: initialTableState,
    fraudReport: initialTableState,
    identityProviders: initialTableState,
    messagingTemplates: initialTableState,
    messageTemplates: initialTableState,
    organizations: initialTableState,
    organizationActivity: initialTableState,
    organizationInvitations: initialTableState,
    organizationUsers: initialTableState,
    organizationUserActivities: initialTableState,
    policyComponentDetailsAssociatedPolicyComponents: initialTableState,
    policyComponents: initialTableState,
    relyingParties: initialTableState,
    securityRules: initialTableState,
    serviceTemplates: initialTableState,
    serviceAssertionPerformance: {
        ...initialTableState,
        sorting: [{
            id: "sap-service",
            desc: true
        }]
    },
    serviceAssertionPerformanceComparison: {
        ...initialTableState,
        sorting: [{
            id: "sap-comparison-service",
            desc: true
        }]
    },
    verificationServices: initialTableState,
    verificationTransactions: {
        ...initialTableState,
        sorting: [{
            id: "date",
            desc: false
        }]
    },
    workflows: initialTableState,
    workflowTemplates: initialTableState
};

export const tableSettingsReducer = (
    state: TableSettingsState = initialTableSettingsState,
    action: TableSettingsActions
): TableSettingsState => {
    switch (action.type) {
        case TableSettingsActionTypes.SET_TABLE_SETTINGS_TABLE_PAGINATION:
            if (
                action.payload.pagination.pageSize
                && action.payload.pagination.pageSize !== state[action.payload.tableSettingsTable].pagination.pageSize
                && !action.payload.ignorePagination
            ) {
                action.payload.pagination.pageIndex = initialTableSettingsState[action.payload.tableSettingsTable].pagination.pageIndex;
            }
            return {
                ...state,
                [action.payload.tableSettingsTable]: {
                    ...state[action.payload.tableSettingsTable],
                    pagination: {
                        ...state[action.payload.tableSettingsTable].pagination,
                        ...action.payload.pagination
                    }
                }
            };
        case TableSettingsActionTypes.SET_TABLE_SETTINGS_TABLE_FILTERS:
            return {
                ...state,
                [action.payload.tableSettingsTable]: {
                    ...state[action.payload.tableSettingsTable],
                    columnFilters: action.payload.columnFilters,
                    pagination: action.payload.ignorePagination
                        ? state[action.payload.tableSettingsTable].pagination
                        : {
                            ...state[action.payload.tableSettingsTable].pagination,
                            pageIndex: initialTableSettingsState[action.payload.tableSettingsTable].pagination.pageIndex
                        }
                }
            };
        case TableSettingsActionTypes.SET_TABLE_SETTINGS_TABLE_SORT_BY:
            return {
                ...state,
                [action.payload.tableSettingsTable]: {
                    ...state[action.payload.tableSettingsTable],
                    sorting: action.payload.sorting,
                    pagination: action.payload.ignorePagination
                        ? state[action.payload.tableSettingsTable].pagination
                        : {
                            ...state[action.payload.tableSettingsTable].pagination,
                            pageIndex: initialTableSettingsState[action.payload.tableSettingsTable].pagination.pageIndex
                        }
                }
            };
        case TableSettingsActionTypes.SET_TABLE_SETTINGS_TABLE_EXPANDED:
            return {
                ...state,
                [action.payload.tableSettingsTable]: {
                    ...state[action.payload.tableSettingsTable],
                    expanded: action.payload.expanded
                }
            };
        case TableSettingsActionTypes.RESET_TABLE_SETTINGS_TABLE_STATE:
            return {
                ...state,
                [action.payload.tableSettingsTable]: {
                    ...state[action.payload.tableSettingsTable],
                    pagination: {
                        ...state[action.payload.tableSettingsTable].pagination,
                        pageIndex: initialTableSettingsState[action.payload.tableSettingsTable].pagination.pageIndex,
                    },
                    columnFilters: initialTableSettingsState[action.payload.tableSettingsTable].columnFilters,
                    sorting: initialTableSettingsState[action.payload.tableSettingsTable].sorting,
                    expanded: initialTableSettingsState[action.payload.tableSettingsTable].expanded
                }
            };
        default:
            return state;
    }
};